import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AlignmentDiagram } from '@/components/result/AlignmentDiagram';
import { ContactForm } from '@/components/result/ContactForm';
import { Button, ButtonLink } from '@/components/ui/Button';
import { useCelebration } from '@/components/ui/Celebration';
import { StickyCta } from '@/components/ui/StickyCta';
import { getCreatorContent } from '@/content/registry';
import { resultCopy } from '@/content/resultCopy';
import { sectionLabel } from '@/content/sections';
import { completedRoundCount, firstUnansweredIndex } from '@/domain/rounds';
import { THEME_LIMITS, byImportance, scoreCompatibility, themesFor } from '@/domain/scoring';
import type { ResultCategory, ResultTheme } from '@/domain/types';
import { track } from '@/lib/analytics';
import { useDocumentTitle } from '@/lib/hooks';
import { clearJourney, loadJourney, setCurrentIndex } from '@/lib/storage';
import styles from './ResultPage.module.css';

/** A small face for each ending. Never a verdict, just a tone of voice. */
const CATEGORY_EMOJI: Record<ResultCategory, string> = {
  'strong-potential': '✨', // ✨
  'worth-a-conversation': '\u{1F9E9}', // 🧩
  'different-paths': '\u{1F9ED}', // 🧭
};

/**
 * One line of the result: the theme, the tier it sat in, and the note.
 *
 * The tier tag is the only place the three sections surface to a reader, and
 * it earns its space by qualifying the note beside it — "we differ" reads very
 * differently under Core than under Preference, and without the tag the list
 * asks her to remember which was which.
 */
function ThemeItem({ theme, aligned }: { theme: ResultTheme; aligned: boolean }) {
  return (
    <li className={`${styles.themeItem} ${aligned ? styles.themeItemAlign : ''}`}>
      <span className={styles.themeName}>
        {theme.theme}
        <span
          className={`${styles.sectionTag} ${
            theme.section === 'core' ? styles.sectionTagCore : ''
          }`}
        >
          {sectionLabel(theme.section)}
        </span>
      </span>
      <span className={styles.themeNote}>{theme.note}</span>
    </li>
  );
}

export function ResultPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const content = getCreatorContent(slug);

  const [formOpen, setFormOpen] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [cleared, setCleared] = useState(false);
  const formRef = useRef<HTMLElement | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);
  const announcedRef = useRef(false);
  const celebratedRef = useRef(false);
  const { celebrate, layer } = useCelebration();

  // Read once on mount. Reading through state means "erase my answers" can
  // re-render this page into its empty state without a reload.
  const saved = useMemo(
    () => (content && !cleared ? loadJourney(content.profile.id) : null),
    [content, cleared],
  );

  const result = useMemo(() => {
    if (!content || !saved) return null;
    return scoreCompatibility(content.questions, saved.answers);
  }, [content, saved]);

  /**
   * What carrying on actually bought her.
   *
   * The optional round used to be invisible in the outcome: nine more answers
   * went into an average that was already being reported through the same
   * three headings, so the page after them looked exactly like the page
   * before. This is the group that changes — the everyday overlap those
   * questions found, minus anything already named above it, so it adds rather
   * than repeats.
   *
   * Built here rather than in `scoreCompatibility` because "which round was a
   * question in" is a fact about how the set is served, and the scoring model
   * deliberately knows nothing about that.
   */
  const everydayThemes = useMemo(() => {
    if (!content || !result) return [];

    const optional = new Set(
      content.rounds.slice(1).flatMap((round) => round.questions.map((q) => q.id)),
    );
    const alreadyNamed = new Set(result.alignmentThemes.map((theme) => theme.questionId));

    const overlap = result.breakdown
      .filter(
        (score) =>
          score.group === 'align' &&
          optional.has(score.questionId) &&
          !alreadyNamed.has(score.questionId),
      )
      .sort(byImportance);

    return themesFor(overlap, content.questions, THEME_LIMITS.everyday);
  }, [content, result]);

  useDocumentTitle(
    content ? `Where we landed — ${content.profile.displayName}` : 'Are We Compatible?',
  );

  /**
   * How many rounds are finished. Computed up here, above the early returns,
   * because `result_shown` has to carry it — a conversion rate for "keep
   * exploring" needs to know how many people were offered the choice, not just
   * how many took it.
   */
  const roundsDone = useMemo(
    () => (content && saved ? completedRoundCount(content.rounds, saved.answers) : 0),
    [content, saved],
  );

  useEffect(() => {
    if (!content || !result || announcedRef.current) return;
    announcedRef.current = true;
    track({
      name: 'result_shown',
      profileId: content.profile.id,
      category: result.category,
      roundsDone,
    });
  }, [content, result, roundsDone]);

  /**
   * The finale. A little richer than a single question's sparkle, and reserved
   * for the ending that actually earned it — the other two categories get
   * their warmth from the copy and the colour, which is where it belongs.
   *
   * The second condition is redundant today, because an unresolved weight-5
   * question already caps the category. It is written out anyway: the rule is
   * "no confetti over an open question about children", and that should not
   * depend on a threshold in another file continuing to imply it.
   */
  useEffect(() => {
    if (!result || celebratedRef.current) return;
    if (result.category !== 'strong-potential') return;
    if (result.hasUnresolvedCoreQuestion) return;
    celebratedRef.current = true;

    const timer = window.setTimeout(() => celebrate(heroRef.current, 'finale'), 260);
    return () => window.clearTimeout(timer);
  }, [result, celebrate]);

  if (!content) return <Navigate to="/404" replace />;

  const { profile, questions, rounds } = content;

  /* --- Arrived without answering ----------------------------------------- */

  if (!saved || !result || result.answeredCount === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          <h1 className={styles.headline}>Nothing to show yet.</h1>
          <p className={styles.lede}>
            {cleared
              ? 'Your answers have been erased from this device, as asked.'
              : 'There are no answers saved in this browser. The questions have not been done here, or they have since been cleared.'}
          </p>
          <ButtonLink to={`/${profile.slug}/questions`} size="large" withArrow>
            {cleared ? 'Start again' : 'Answer the questions'}
          </ButtonLink>
        </div>
      </div>
    );
  }

  const copy = resultCopy[result.category];
  const canContact = copy.nextStep.cta !== null;

  /* --- Is there more to ask? ---------------------------------------------
     A result computed from one round is a real result — `scoreCompatibility`
     averages over answered questions only — so this is genuinely an offer and
     never a nag. `nextIndex` is where the journey would resume: the first
     question with no answer, which is not necessarily the start of the next
     round if she skipped back and left a gap. */
  const nextIndex = firstUnansweredIndex(questions, saved.answers);
  const nextRound = nextIndex === null ? undefined : rounds[roundsDone];
  const remainingCount = questions.length - result.answeredCount;

  const keepExploring = () => {
    if (nextIndex === null) return;
    setCurrentIndex(profile.id, nextIndex);
    track({ name: 'journey_continued', profileId: profile.id, roundIndex: roundsDone });
    // Flagged so the journey does not greet her with "picked up where you left
    // off". She has not been anywhere — she pressed a button one screen ago.
    navigate(`/${profile.slug}/questions`, { state: { continued: true } });
  };

  const openForm = (source: 'inline' | 'sticky') => {
    setFormOpen(true);
    setDeclined(false);
    track({
      name: 'contact_form_opened',
      profileId: profile.id,
      category: result.category,
      source,
    });
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const restart = () => {
    clearJourney(profile.id);
    track({ name: 'journey_restarted', profileId: profile.id });
    navigate(`/${profile.slug}/questions`);
  };

  const eraseAnswers = () => {
    clearJourney(profile.id);
    setCleared(true);
  };

  return (
    <div className={styles.page}>
      {/* --- Headline ------------------------------------------------------ */}
      <section className={styles.hero} ref={heroRef}>
        <p className={styles.eyebrow}>
          <span aria-hidden="true">{CATEGORY_EMOJI[result.category]}</span>
          <span>{copy.eyebrow}</span>
        </p>
        <h1 className={styles.headline}>{copy.headline}</h1>
        <AlignmentDiagram
          category={result.category}
          score={result.score}
          hasConflict={result.hasNonNegotiableConflict}
        />
        <p className={styles.lede}>{copy.lede}</p>
      </section>

      {/* --- Themes -------------------------------------------------------- */}
      <section className={styles.themes} aria-label="What the answers showed">
        {result.hasNonNegotiableConflict ? (
          <div className={styles.conflict}>
            <h2 className={styles.conflictHeading}>The honest part</h2>
            <ul className={styles.conflictList}>
              {result.nonNegotiableConflicts.map((conflict) => (
                <li key={conflict}>
                  <span className={styles.conflictGlyph} aria-hidden="true">
                    ●
                  </span>
                  <span>On {conflict}.</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {result.alignmentThemes.length > 0 ? (
          <div className={styles.themeBlock}>
            <h2 className={styles.themeHeading}>{copy.alignmentHeading}</h2>
            <ul className={styles.themeList}>
              {result.alignmentThemes.map((theme) => (
                <ThemeItem key={theme.questionId} theme={theme} aligned />
              ))}
            </ul>
          </div>
        ) : null}

        {/* --- Worth talking about ------------------------------------------
            Nearly-agreements on the three questions that decide things. Its
            own block rather than a line in either list beside it, because it
            is neither: "I could see it with the right person" is not a
            difference and it is very definitely not a yes. */}
        {result.conversationThemes.length > 0 ? (
          <div className={styles.themeBlock}>
            <h2 className={styles.themeHeading}>{copy.conversationHeading}</h2>
            <ul className={styles.themeList}>
              {result.conversationThemes.map((theme) => (
                <ThemeItem key={theme.questionId} theme={theme} aligned={false} />
              ))}
            </ul>
          </div>
        ) : null}

        {result.differenceThemes.length > 0 ? (
          <div className={styles.themeBlock}>
            <h2 className={styles.themeHeading}>{copy.differenceHeading}</h2>
            <ul className={styles.themeList}>
              {result.differenceThemes.map((theme) => (
                <ThemeItem key={theme.questionId} theme={theme} aligned={false} />
              ))}
            </ul>
          </div>
        ) : null}

        {result.conversationThemes.length === 0 && result.differenceThemes.length === 0 ? (
          /*
            "Nothing here stood out as a difference worth flagging" claimed a
            judgement nobody made — a handful of questions cannot see far
            enough to declare a person free of differences. This says only what
            is true: these answers did not surface any.
          */
          <p className={styles.emptyNote}>
            No unresolved differences surfaced in these answers.
          </p>
        ) : null}

        {/* --- What the optional round added --------------------------------
            Rendered only when there is something in it, which means only after
            she has carried on. Nine more answers ought to visibly change the
            page they were given to; before this they changed an average and
            nothing else. */}
        {everydayThemes.length > 0 ? (
          <div className={styles.themeBlock}>
            <h2 className={styles.themeHeading}>{copy.everydayHeading}</h2>
            <p className={styles.themeBlockNote}>
              What the extra questions turned up, on top of everything above.
            </p>
            <ul className={styles.themeList}>
              {everydayThemes.map((theme) => (
                <ThemeItem key={theme.questionId} theme={theme} aligned />
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {/* --- Caveat -------------------------------------------------------- */}
      <p className={styles.caveat}>{copy.caveat}</p>

      {/* --- Next step ------------------------------------------------------ */}
      <section ref={formRef}>
        {canContact ? (
          <div className={styles.nextStep}>
            <h2 className={styles.nextStepHeading}>{copy.nextStep.heading}</h2>
            <p className={styles.nextStepBody}>{copy.nextStep.body}</p>

            {formOpen ? (
              <ContactForm
                profile={profile}
                questions={questions}
                answers={saved.answers}
                result={result}
              />
            ) : declined ? (
              /*
                Declining is a decision, not a door locking behind her. It is
                one tap, taken in a second, and quite often taken before
                reading the rest of the page — so the way back has to be
                visible rather than a page reload she has to think of herself.
              */
              <div className={styles.declined}>
                <p className={styles.declineRow}>{copy.decline}</p>
                <p className={styles.declineRow}>
                  <Button variant="ghost" size="small" onClick={() => setDeclined(false)}>
                    Actually, put that back
                  </Button>
                </p>
              </div>
            ) : (
              <>
                <Button size="large" block onClick={() => openForm('inline')} withArrow>
                  {copy.nextStep.cta}
                </Button>
                <p className={styles.declineRow}>
                  <Button variant="ghost" size="small" onClick={() => setDeclined(true)}>
                    No thanks
                  </Button>
                </p>
              </>
            )}
          </div>
        ) : (
          <div className={styles.closing}>
            <h2 className={styles.nextStepHeading}>{copy.nextStep.heading}</h2>
            <p className={styles.nextStepBody}>{copy.nextStep.body}</p>
          </div>
        )}
      </section>

      {/* --- More questions, if she wants them --------------------------------
          Under the invitation rather than above it, deliberately. Getting in
          touch is the point of the page; more questions is the alternative for
          someone who is interested but not ready, and putting it first would
          read as "not yet, answer these". */}
      {nextRound && nextIndex !== null ? (
        <section className={styles.more}>
          <p className={styles.moreEyebrow}>{nextRound.meta.label}</p>
          <h2 className={styles.moreHeading}>
            {remainingCount} more {remainingCount === 1 ? 'question' : 'questions'}, if
            you are curious.
          </h2>
          {/*
            This used to promise "everything above stays as it is — carrying on
            only adds to it", which is not true and is exactly the kind of
            untrue the rest of this page was rewritten to remove. The score is a
            weighted average over every answered question, so seven substantial
            differences really can move a strong-potential result down to
            worth-a-conversation. What is actually guaranteed is that the
            answers already given keep counting — they are not discarded or
            re-weighted — so that is what it now says.
          */}
          <p className={styles.moreBody}>
            {nextRound.meta.blurb} Your first {result.answeredCount} answers stay part
            of the result; carrying on adds more context about everyday life.
          </p>
          <Button variant="secondary" size="large" onClick={keepExploring} withArrow>
            Keep exploring
          </Button>
        </section>
      ) : null}

      {/* --- Controls -------------------------------------------------------- */}
      <section className={styles.controls}>
        <div className={styles.controlsRow}>
          <Button variant="secondary" onClick={restart}>
            Start again
          </Button>
          <ButtonLink to={`/${profile.slug}`} variant="secondary">
            Back to {profile.displayName}&rsquo;s page
          </ButtonLink>
          <Button variant="ghost" onClick={eraseAnswers}>
            Erase my answers
          </Button>
        </div>
        <p className={styles.controlsNote}>
          Your answers are stored only in this browser. Erasing them removes them from
          this device; nothing was ever sent anywhere unless you chose to send it.
        </p>
      </section>

      {/* --- Sticky invitation ------------------------------------------------
          Up for the whole stretch of the page where the real invitation is out
          of reach. It retires as soon as that card is on screen, and for good
          once the form is open or the offer declined. */}
      <StickyCta standInFor={formRef} enabled={canContact && !formOpen && !declined}>
        <Button size="large" block onClick={() => openForm('sticky')} withArrow>
          {copy.nextStep.cta}
        </Button>
      </StickyCta>

      {layer}
    </div>
  );
}
